<template>
  <div
    v-if="isSuspended"
    class="error"
    :class="panelsClass"
  >
    <h2 class="light-text">
      <Icon
        name="forbidden"
        fill="white"
        class="pull-right"
      />
      <span v-translate>Payouts are <strong>not available</strong> while your account is <strong>suspended</strong>.</span>
    </h2>
    <div class="row no-space justify-between items-end">
      <p class="col-8 no-space">
        <span v-translate>If you'd like to appeal your suspension, please contact</span>&nbsp;
        <a
          href="mailto:investigations@dittomusic.com"
          class="text-white"
        >investigations@dittomusic.com</a>
      </p>
      <a
        v-translate
        :href="whySuspendedLink"
        target="_blank"
        class="faded-link"
      >
        Why we suspend accounts
      </a>
    </div>
  </div>

  <div
    v-else-if="!isUserEmailVerified"
    class="warning"
    :class="panelsClass"
  >
    <h2
      v-translate
      class="light-text"
    >
      Please confirm your <strong>email address.</strong>
    </h2>
    <div class="row no-space justify-between items-end">
      <div class="col-8-small">
        <p
          v-translate
          class="no-space"
        >
          We've sent you an email to confirm your address. Click the link in the email and come back to set up your payout method.
        </p>
        <Notification
          v-if="emailResentError"
          type="error"
          icon-name="danger"
          icon-fill="white"
          :title="$gettext('An error occurred, please try again')"
          :message="emailResentError"
        />
      </div>
      <div class="text-right">
        <button
          v-if="!userEmailResent"
          v-translate
          class="white"
          @click="resendUserEmail"
        >
          Resend email
        </button>
        <button
          v-else
          v-translate
          disabled
        >
          Email resent
        </button>
      </div>
    </div>
  </div>

  <div
    v-else-if="userKycStatus === kycStatus.required"
    class="primary"
    :class="panelsClass"
  >
    <div class="row no-space justify-between items-end">
      <div class="col-8">
        <h2
          v-translate
          class="light-text"
        >
          We just need to <strong>confirm your identity</strong>.
        </h2>
        <p class="no-space">
          {{ $gettext("Please complete this quick, one-time identity check. It'll only take a few minutes and once you're verified we can start sending royalties!") }}
        </p>
      </div>
      <div class="text-right">
        <VerifyMeButton />
      </div>
    </div>
  </div>

  <div
    v-else-if="userKycStatus === kycStatus.inProgress"
    class="warning"
    :class="panelsClass"
  >
    <div class="row no-space justify-between items-end">
      <div class="col-6-large">
        <h2
          v-translate
          class="light-text"
        >
          Your identity check is <strong>in progress…</strong>.
        </h2>
        <p
          v-translate
          class="no-space"
        >
          It will only take a few minutes. You may need to refresh this page.
        </p>
      </div>
    </div>
  </div>

  <div
    v-else-if="userKycStatus === kycStatus.rejected"
    class="error"
    :class="panelsClass"
  >
    <div class="row no-space justify-between items-end">
      <div class="col-7">
        <h2
          v-translate
          class="light-text"
        >
          Your identity check was <strong>unsuccessful</strong>.
        </h2>
        <p
          v-translate
          class="no-space"
        >
          Please try again, or check our FAQs for help on how to verify your account.
        </p>
      </div>
      <div class="text-right">
        <VerifyMeButton @finished-sdk="$emit('verification-updated')" />
      </div>
    </div>
  </div>

  <LoaderErrorBlock
    v-else-if="hasSubscriptionError"
    :error-title="$gettext('Oops! We couldn’t load this right now.')"
    :error-message="$gettext('Please refresh the page or try again later.')"
  />

  <div
    v-else-if="isSubscriptionMissing"
    class="warning"
    :class="panelsClass"
  >
    <div class="col-9">
      <h2
        v-translate
        class="light-text"
      >
        You need an <strong>active subscription</strong> to make payout requests.
      </h2>
    </div>
    <div class="text-right">
      <a
        v-translate
        :href="`${dashboardUrl}/subscriptions`"
        class="button white pull-right"
      >Pick a plan</a>
    </div>
  </div>

  <div
    v-else-if="unverifiedActivePayoutMethods.length"
    class="warning"
    :class="panelsClass"
  >
    <div class="row no-space justify-between items-end">
      <div class="col-8-small">
        <h2 class="light-text">
          {{ $gettext('We\'ve emailed you to confirm your new') }}
          <strong>{{ unverifiedActivePayoutMethods[0].name }}</strong>
          {{ $gettext('payout method.') }}
        </h2>
        <p
          v-translate
          class="no-space"
        >
          Make sure to check your spam folder. If it hasn’t arrived, try resending the email.
        </p>
        <Notification
          v-if="emailResentError"
          type="error"
          icon-name="danger"
          icon-fill="white"
          :title="$gettext('An error occurred, please try again')"
          :message="emailResentError"
        />
      </div>
      <div class="text-right">
        <button
          v-if="!payoutEmailResent"
          v-translate
          class="white"
          @click="resendPayoutEmail(unverifiedActivePayoutMethods[0].id)"
        >
          Resend email
        </button>
        <button
          v-else
          v-translate
          disabled
        >
          Email resent
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { KycStatus } from '@/constants'
import Icon from '@/components/CommonUI/Icon.vue'
import LoaderErrorBlock from '@/components/CommonUI/LoaderErrorBlock.vue'
import Notification from '@/components/CommonUI/Notification.vue'
import VerifyMeButton from '@/components/VerifyMeButton.vue'
import { getErrorMessage } from '@/helpers/errorHandler'

defineEmits(['verification-updated'])

const store = useStore()

const dashboardUrl = process.env.VUE_APP_DASHBOARD_URL || ''
const whySuspendedLink = 'https://support.dittomusic.com/en/articles/4283888-why-has-my-account-been-suspended#'
const kycStatus = KycStatus
const panelsClass = 'verification panel elevate'

const payoutEmailResent = ref(false)
const userEmailResent = ref(false)
const emailResentError = ref<string | null>(null)

const userKycStatus = computed(() => store.state.user.status?.kyc)
const hasSubscriptionError = computed(() => store.state.hasSubscriptionError)

const isSubscriptionMissing = computed(() => store.getters.isSubscriptionMissing)
const isSuspended = computed(() => store.getters.isSuspended)
const isUserEmailVerified = computed(() => store.getters.isUserEmailVerified)
const unverifiedActivePayoutMethods = computed(() => store.getters.unverifiedActivePayoutMethods)

async function resendUserEmail() {
  try {
    emailResentError.value = null
    await store.dispatch('resendUserVerificationEmail')
    userEmailResent.value = true
  } catch (error) {
    emailResentError.value = getErrorMessage(error)
  }
}

async function resendPayoutEmail(payoutMethodId: number) {
  try {
    emailResentError.value = null
    await store.dispatch('resendPayoutVerificationEmail', payoutMethodId)
    payoutEmailResent.value = true
  } catch (error) {
    emailResentError.value = getErrorMessage(error)
  }
}
</script>

<style lang="scss">
.verification.panel {
  h2 {
    font-size: 2rem;
  }

  .icon.pull-right {
    margin-left: 4rem;
  }

  .light-text strong {
    font-weight: 700;
  }

  a:not(.button):hover {
    color: white;
    font-weight: 400;
    text-decoration: none;
  }
}
.faded-link {
  color: #E0A5A5;
}
</style>
